import SitePhoto from '@/components/Photo'
import { Ctas, HERO_PHOTO, SQUARE_PHOTO, type SectionOf } from './shared'

export default function HeroSection({ section }: { section: SectionOf<'hero'> }) {
  // The eyebrow usually opens the hero, above the headline. On a hero that
  // also carries a `micro` line — today only the home page — it moves down
  // and sits directly under the sub paragraph, so "#ScaleValue" reads as
  // the name for the sentence that just described it rather than as a label
  // arriving before anything has been said. Everywhere else nothing moves.
  const eyebrowUnderSub = Boolean(section.eyebrow && section.micro)
  // Above the headline it is centred on its own copy block and set larger
  // than a normal kicker (asked for 2026-08-26, with the three solution
  // heroes side by side: "Discover / Deploy / Outcome" are the three steps
  // of one sequence, and at 0.875rem pinned to the left margin they read as
  // a caption on the headline rather than as the name of the stage). Asked
  // for a second time the same day with the same screenshot, so it took one
  // more step — 1.5rem desktop / 1.125rem mobile, half the h1 — which is as
  // far as an uppercase label can go before it competes with the headline
  // it introduces. If a third round arrives, move the headline down rather
  // than the label up. **Those three heroes dropped their eyebrow entirely
  // on 2026-08-28** (*"her bir ayrı sayfadaki discover, deploy outcome
  // başlıkları olmasına gerek yok"* — the stage is already the card kicker
  // on the hub, so repeating it above the headline said nothing new), so
  // this centred form now only serves the hub, About, Tools and Partner
  // Program heroes. It is kept rather than reverted because nobody has
  // asked about those four; revert it here if they should go back to the
  // small left kicker. The
  // block is the copy column — the left half of a hero with a photograph,
  // the `max-w-3xl` measure on one without — so it centres over the words
  // and not over the page. Below the sub it is the home page's "#ScaleValue"
  // and nothing about that moves: it belongs to the paragraph above it.
  const eyebrow = section.eyebrow ? (
    <p
      className={
        eyebrowUnderSub
          ? 'text-sm font-semibold uppercase tracking-[0.14em] text-primary'
          : 'text-center text-lg font-semibold uppercase tracking-[0.14em] text-primary sm:text-2xl'
      }
    >
      {section.eyebrow}
    </p>
  ) : null
  // A title-only opener is centred on the page rather than pinned to the left
  // margin — see `align` in content/sections.ts. `text-center` rather than a
  // per-element class so the sub, the actions and the micro line follow the
  // headline; `mx-auto` is what actually centres the `max-w-3xl` measure, which
  // is otherwise a left column with white space to its right.
  const centred = section.align === 'center' && !section.photo
  const copy = (
    <div
      className={
        section.photo ? undefined : `max-w-3xl${centred ? ' mx-auto text-center' : ''}`
      }
    >
      {!eyebrowUnderSub && eyebrow}
      {/* A little more air under the bigger label, so it stays a separate
          line rather than crowding the headline. */}
      {/* `whitespace-pre-line` so a headline can force its own break with a
          `\n` — the home page's second sentence starts a line of its own
          rather than wrapping mid-clause. Headlines without a newline wrap
          exactly as before; pre-line only adds the explicit breaks. */}
      <h1
        className={`whitespace-pre-line${
          section.eyebrow && !eyebrowUnderSub ? ' mt-5' : ''
        }`}
      >
        {section.headline}
      </h1>
      {/* Bold only when there is supporting prose under it to be separated
          from — see `body` in content/sections.ts. */}
      {section.sub && (
        <p className={`mt-8 text-lg leading-relaxed ${section.body ? 'font-bold' : ''}`}>
          {section.sub}
        </p>
      )}
      {section.body?.map((p) => (
        <p key={p.slice(0, 40)} className="mt-5 text-lg leading-relaxed">
          {p}
        </p>
      ))}
      {eyebrowUnderSub && <div className="mt-4">{eyebrow}</div>}
      {/* Two buttons only fit beside each other in this column at the
          trimmed size; a lone hero CTA has room and stays full size. */}
      <Ctas items={section.ctas} tight={(section.ctas?.length ?? 0) > 1} secondaryLight />
      {section.micro && (
        <p className="mt-6 text-base font-bold uppercase tracking-[0.14em] text-muted-foreground">
          {section.micro}
        </p>
      )}
    </div>
  )

  if (!section.photo) return copy

  // Copy left, photograph right, stacked below `lg`. `items-center` rather
  // than `items-start`: the photographs are landscape and shorter than the
  // copy column, and pinning them to the top leaves a hole under the image
  // that reads as a mistake.
  //
  // The photograph takes slightly more than half the row, and is framed by
  // `HERO_PHOTO` — the one hero frame, defined at the top of this file, so
  // every page's hero is the same box as the home page's. Squarer and taller
  // than the sources also means it comes closer to filling the height of the
  // copy beside it, which is the hole `items-center` was working around.
  return (
    <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-16">
      {copy}
      <SitePhoto
        photo={section.photo}
        priority
        // Just over half the container above `lg`, all of it below. Without
        // this the browser assumes full width and every phone downloads the
        // 1280 file to paint 640 pixels.
        sizes="(min-width: 1024px) 52vw, 92vw"
        // A square canvas keeps its own shape rather than losing a third of
        // its height to the 3:2 box — see `photoFrame` in content/sections.ts.
        className={section.photoFrame === 'square' ? SQUARE_PHOTO : HERO_PHOTO}
      />
    </div>
  )
}
